import { defineStore } from "pinia";
import AppConfigService from "@/services/AppConfigService";

// Klucz cache'u flag w `sessionStorage` — ta sama „szuflada" co token sesji (`cvToken`),
// więc zasięg jest identyczny: jedna karta, znika po jej zamknięciu.
const FLAGS_STORAGE_KEY = "cvFeatureFlags";

// Bezpieczny domyślny zestaw: wszystko wyłączone, dopóki backend nie powie inaczej.
const DEFAULT_FLAGS = {
  locationManagement: false,
  monitoringAlerts: false,
};

// Odczyt flag zapamiętanych przy poprzednim żądaniu w tej karcie. Dzięki temu menu
// renderuje się w prawidłowym kształcie już przy PIERWSZYM malowaniu po odświeżeniu
// strony — bez tego pozycje zależne od flag (np. „Zarządzanie lokalizacjami")
// doklejały się do menu dopiero po odpowiedzi z `/app/feature-flags`.
// Wartość z cache'u jest zawsze weryfikowana żądaniem, a wejście na trasę z
// `requiresFeature` czeka w guardzie na świeżą odpowiedź, więc cache nie może
// samodzielnie wpuścić nikogo do wyłączonej funkcji.
function readCachedFlags() {
  try {
    const raw = sessionStorage.getItem(FLAGS_STORAGE_KEY);
    if (!raw) {
      return { ...DEFAULT_FLAGS };
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object"
      ? { ...DEFAULT_FLAGS, ...parsed }
      : { ...DEFAULT_FLAGS };
  } catch {
    // Uszkodzony wpis / brak dostępu do storage — startujemy z wyłączonymi flagami.
    return { ...DEFAULT_FLAGS };
  }
}

function writeCachedFlags(flags) {
  try {
    sessionStorage.setItem(FLAGS_STORAGE_KEY, JSON.stringify(flags));
  } catch {
    // Cache jest opcjonalny (tryb prywatny, brak miejsca) — brak zapisu nic nie psuje.
  }
}

// Store przechowujący konfigurację aplikacji pobraną z backendu — przede wszystkim flagi funkcji
// (parametryzowane po stronie backendu w .env / .env.local). Ładowany raz po zalogowaniu.
export const useConfigStore = defineStore({
  id: "config",
  state: () => ({
    // Flagi funkcji. Start z cache'u tej karty (patrz `readCachedFlags`), a przy pierwszym
    // uruchomieniu — wszystko wyłączone, aż do pobrania z API.
    featureFlags: readCachedFlags(),
    _loaded: false,
    _loadPromise: null,
  }),
  actions: {
    // Przyjmuje flagi dostarczone razem z profilem użytkownika (`GET /users/self`).
    //
    // To jest PODSTAWOWA droga zasilania store'a: profil i tak jest pobierany przed
    // nawigacją po logowaniu, więc menu dostaje komplet danych bez ani jednego
    // dodatkowego żądania i bez wydłużania logowania. `loadFeatureFlags()` zostaje
    // tylko jako fallback (backend bez `featureFlags` w profilu).
    //
    // Zwraca true, jeśli flagi zostały przyjęte.
    applyFlags(flags) {
      if (!flags || typeof flags !== "object" || Array.isArray(flags)) {
        return false;
      }
      this.featureFlags = {
        ...this.featureFlags,
        ...flags,
      };
      this._loaded = true;
      writeCachedFlags(this.featureFlags);
      return true;
    },
    // Pobiera flagi funkcji z backendu. Idempotentne — pobiera tylko raz (chyba że force=true).
    // Ścieżka awaryjna: normalnie flagi przychodzą z profilem (`applyFlags`), więc przy
    // ustawionym `_loaded` ta akcja nie wykonuje żądania.
    async loadFeatureFlags(force = false) {
      if (!force && this._loaded) {
        return this.featureFlags;
      }
      if (!force && this._loadPromise) {
        return this._loadPromise;
      }

      this._loadPromise = (async () => {
        try {
          const flags = await AppConfigService.getFeatureFlags();
          this.featureFlags = {
            ...this.featureFlags,
            ...(flags || {}),
          };
          this._loaded = true;
          writeCachedFlags(this.featureFlags);
        } catch (error) {
          // Brak flag = zostajemy przy tym, co już mamy (cache albo domyślne wyłączone).
          // Nie blokujemy działania aplikacji i nie zerujemy `_loaded`, więc kolejne
          // wejście na trasę / montaż menu spróbuje ponownie.
          console.error("Nie udało się pobrać flag funkcji:", error);
        }
        return this.featureFlags;
      })();

      try {
        return await this._loadPromise;
      } finally {
        this._loadPromise = null;
      }
    },
    // Reset stanu (np. przy wylogowaniu).
    reset() {
      this.featureFlags = { ...DEFAULT_FLAGS };
      this._loaded = false;
      this._loadPromise = null;
      try {
        sessionStorage.removeItem(FLAGS_STORAGE_KEY);
      } catch {
        // brak dostępu do storage — nie ma czego czyścić
      }
    },
  },
});
